/**
 * Admin → Purposes
 *
 * Phase 5b item 6 (2026-05-16). Lists every purpose mapping for the
 * active tenant - i.e. the (source × identifier → purpose / legal-basis
 * / confidence) tuples that resolve "what is this query for?" at
 * scan-time. Used by the Detection module's purpose-binding decisions.
 *
 * Read-only list view. Create + resolve happens via separate wizards
 * (next sub-deliverable, batched with CustomRoles author flow).
 *
 * Backend:
 *   GET /v1/foundational/purposes → { purposeMappings: [...] }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  Filter,
  Target,
  Workflow,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { listPurposes, type PurposeMapping, type PurposeSourceType, type PurposeConfidence } from '@/lib/api';

type SourceFilter = PurposeSourceType | 'all';

const SOURCE_LABELS: Record<PurposeSourceType, string> = {
  application: 'Application',
  endpoint: 'Endpoint',
  service_account: 'Service account',
  role: 'Role',
  sql_tag: 'SQL tag',
  bi_report: 'BI report',
  ropa_id: 'RoPA id',
  pia_id: 'PIA id',
};

const SOURCE_OPTIONS: ReadonlyArray<{ value: SourceFilter; label: string }> = [
  { value: 'all', label: 'All sources' },
  { value: 'application', label: 'Applications' },
  { value: 'endpoint', label: 'Endpoints' },
  { value: 'service_account', label: 'Service accounts' },
  { value: 'role', label: 'Roles' },
  { value: 'sql_tag', label: 'SQL tags' },
  { value: 'bi_report', label: 'BI reports' },
  { value: 'ropa_id', label: 'RoPA ids' },
  { value: 'pia_id', label: 'PIA ids' },
];

const CONFIDENCE_VARIANT: Record<PurposeConfidence, 'success' | 'warning' | 'critical'> = {
  high: 'success',
  medium: 'warning',
  low: 'critical',
};

export function AdminPurposes(): JSX.Element {
  const [purposes, setPurposes] = useState<PurposeMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listPurposes();
      setPurposes(r.purposeMappings);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load purposes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visible = useMemo(() => {
    if (sourceFilter === 'all') return purposes;
    return purposes.filter((p) => p.sourceType === sourceFilter);
  }, [purposes, sourceFilter]);

  const stats = useMemo(() => {
    const bySrc = new Map<PurposeSourceType, number>();
    const byConf: Record<PurposeConfidence, number> = { high: 0, medium: 0, low: 0 };
    for (const p of purposes) {
      bySrc.set(p.sourceType, (bySrc.get(p.sourceType) ?? 0) + 1);
      byConf[p.confidence] += 1;
    }
    return { bySrc, byConf };
  }, [purposes]);

  return (
    <PageContainer>
      <PageHeader
        title="Purposes"
        subtitle="Source → purpose bindings that drive purpose-binding decisions at scan time. RoPA + PIA cross-reference belongs here."
        badges={[
          { label: 'DPDPA Section 4-6', variant: 'glass' },
          { label: 'RoPA + PIA bound', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Failed to load purposes"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Purpose mappings" value={purposes.length} icon={Target} hint="across all sources" />
        <KpiCard label="High confidence" value={stats.byConf.high} icon={Workflow} glow="high" hint="verified bindings" />
        <KpiCard label="Medium / Low" value={stats.byConf.medium + stats.byConf.low} icon={AlertTriangle} glow={stats.byConf.low > 0 ? 'critical' : 'medium'} hint="needs review" />
        <KpiCard label="Distinct source kinds" value={stats.bySrc.size} icon={Filter} hint="of 8 possible" />
      </div>

      <GlassCard className="p-6">
        <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Purpose mappings</CardTitle>
            <CardDescription>
              The map of "what is this source for?" Detection&rsquo;s purpose-binding decisions read
              this catalogue at runtime to allow or block cross-purpose data reads.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </CardHeader>

        {loading && <LoadingState caption="Loading purposes…" />}

        {!loading && visible.length === 0 && (
          <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
            <Target className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-foreground/90">No purpose mappings on file</div>
            <div className="mt-1">
              {sourceFilter === 'all'
                ? 'No purposes have been bound yet - purpose resolution falls back to per-data-class policy defaults.'
                : `No ${SOURCE_LABELS[sourceFilter as PurposeSourceType]?.toLowerCase() ?? sourceFilter} mappings; try “All sources”.`}
            </div>
          </div>
        )}

        {!loading && visible.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/30">
                  <th className="py-2 pr-3 font-normal">Source kind</th>
                  <th className="py-2 pr-3 font-normal">Identifier</th>
                  <th className="py-2 pr-3 font-normal">Purpose</th>
                  <th className="py-2 pr-3 font-normal">Legal basis</th>
                  <th className="py-2 pr-3 font-normal">Confidence</th>
                  <th className="py-2 pr-3 font-normal">Evidence</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Updated</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p, i) => (
                  <motion.tr
                    key={p.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.01, 0.3) }}
                    className="border-b border-border/15 hover:bg-muted/20 align-top"
                  >
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <Badge variant="glass" className="text-[10px]">{SOURCE_LABELS[p.sourceType]}</Badge>
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px] max-w-[20ch] truncate" title={p.sourceIdentifier}>
                      {p.sourceIdentifier}
                    </td>
                    <td className="py-2 pr-3">{p.purposeName}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{p.legalBasis ?? '-'}</td>
                    <td className="py-2 pr-3">
                      <Badge variant={CONFIDENCE_VARIANT[p.confidence]} className="text-[10px]">
                        {p.confidence}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground max-w-[24ch] truncate" title={p.evidence ?? ''}>
                      {p.evidence ?? '-'}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                      {new Date(p.updatedAt).toLocaleDateString()}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </PageContainer>
  );
}
